import { useState, useRef, FormEvent } from 'react';
import { ActiveFilter } from '../../crawler/crawler.interface';
import Button from '../button/button';
import MultiChipSelect, { MultiChipSelectOption } from '../filters/multiChipSelect/multiChipSelect';
import { LeftArrow } from '../icons/icons';
import TypeAheadSearch from '../typeaheadSearch/typeaheadSearch';
import './filter.scss';
import MultiSelector from '../multiSelector/multiSelector';
import PageTypeFilter from '../filters/pageTypeFilter/pageTypeFilter';
import CheckboxFilter from '../filters/checkboxFilter/checkboxFilter';

export interface FilterProps {
    title: string;
    label: string;
    name: string;
    radioButtonOptions?: RadioButtonOption[];
    defaultRadioSelection?: RadioButtonOption;
    multiChipOptions?: MultiChipSelectOption[];
    defaultSelectedChips?: MultiChipSelectOption[];
    defaultConfidenceLevel?: MultiChipSelectOption;
    typeAheadOptions?: string[];
    checkboxOptions?: string[];
    defaultCheckedOptions?: string;
    filterType: string;
    onSubmit?: (filter: ActiveFilter) => void;
    closeFilter: () => void;
}

export interface RadioButtonOption {
    label: string;
    value: string;
    checked: boolean;
}

const Filter = ({
    defaultSelectedChips = [],
    title,
    label,
    name,
    filterType,
    typeAheadOptions = [],
    radioButtonOptions,
    defaultRadioSelection,
    defaultConfidenceLevel,
    multiChipOptions,
    checkboxOptions,
    defaultCheckedOptions,
    onSubmit,
    closeFilter }: FilterProps) => {

    const [textFilterVal, setTextFilter] = useState<string>('');
    const [selectedRadioButton, setRadioButton] = useState<RadioButtonOption>(() => checkForDefaultRadioVal());
    const [selectedTypeAheadMulti, setSelectedTypeAheadMulti] = useState<string[]>([]);

    const textInputRef = useRef<HTMLInputElement>(null);

    function checkForDefaultRadioVal() {
        return defaultRadioSelection ? defaultRadioSelection : { label: '', value: '', checked: true };
    }

    function submitFilter(filter: ActiveFilter, e?: FormEvent) {
        e?.preventDefault();
        if (onSubmit) {
            switch (filterType) {
                case 'text':
                    submitTextFilter(filter, onSubmit);
                    break;
                case 'radio':
                    submitRadioFilter(filter, onSubmit);
                    break;
                case 'multi-chip':
                    onSubmit(filter);
                    break;
                case 'page-type-filter':
                    onSubmit(filter);
                    break;
                case 'multi-select':
                    onSubmit(filter);
                    break;
                case 'type-ahead':
                    onSubmit(filter);
                    break;
                case 'checkbox':
                    onSubmit(filter);
                    break;
                default:
                    return;
            }
        }
    }

    function submitTextFilter(filter: ActiveFilter, submit: (filter: ActiveFilter) => void) {
        const inputEl = textInputRef.current;
        if (textFilterVal.length) {
            submit(filter);
        } else {
            inputEl != null ? inputEl.className += ' error' : null;
        }
    }

    function submitRadioFilter(filter: ActiveFilter, submit: (filter: ActiveFilter) => void) {
        if (selectedRadioButton) {
            submit(filter);
        }
    }

    function submitTypeAhead() {
        const owners = selectedTypeAheadMulti.join('|');
        const ownerFilter: ActiveFilter = { name: 'owner', value: owners };
        submitFilter(ownerFilter);
    }

    function handleRadioChange(e: React.ChangeEvent<HTMLInputElement>) {
        const name = e.target.name;
        const val = e.target.value;
        setRadioButton({ label: name, value: val, checked: true });
    }

    function chooseFilter() {
        let filter;
        switch (filterType) {
            case 'text':
                filter =
                    <form className="text-filter" onSubmit={(e: FormEvent) => submitFilter({ name: name, value: textFilterVal }, e)}>
                        <label htmlFor="text-input">{label}</label>
                        <input id="text-input" className="text-input" ref={textInputRef} type="text" value={textFilterVal} onChange={(e) => setTextFilter(e.target.value)} />
                        <Button variant="button button--small" type="submit">Submit</Button>
                    </form>;
                break;
            case 'radio':
                filter =
                    <div className="radio-button-filter" aria-label={label}>
                        {radioButtonOptions?.map((button: RadioButtonOption) => (
                            <span key={button.value}>
                                <input type="radio"
                                    id={"radio-input-" + button.value}
                                    role="radio-button"
                                    value={button.value}
                                    name={button.label}
                                    checked={selectedRadioButton?.label === button.label}
                                    aria-label={button.label}
                                    onChange={handleRadioChange} />
                                <label htmlFor={"radio-input-" + button.value}> {button.label}</label>
                            </span>
                        ))}
                        <div className="center-button">
                            <Button variant="button button--small" onClick={() => submitFilter({ name: name, value: selectedRadioButton.value })}>Submit</Button>
                        </div>
                    </div >;
                break;
            case 'multi-chip':
                filter =
                    <div className={"multi-chip-select-filter " + name}>
                        {multiChipOptions &&
                            <MultiChipSelect defaultSelectedChips={defaultSelectedChips} name={name} options={multiChipOptions} handleSubmit={submitFilter} />
                        }
                    </div>;
                break;
            case 'page-type-filter':
                filter =
                    <div className={"multi-chip-select-filter " + name}>
                        {multiChipOptions &&
                            <PageTypeFilter name={name} defaultSelectedPageTypeOptions={defaultSelectedChips} defaultConfidenceLevel={defaultConfidenceLevel} options={multiChipOptions} handleSubmit={submitFilter} />
                        }
                    </div>;
                break;
            case 'multi-select':
                filter =
                    <div className="multiselector typeahead">
                        <MultiSelector setParentTags={setSelectedTypeAheadMulti} submitTypeAhead={submitTypeAhead} inputClass={"typeahead-multiselector"} isTypeAhead={true} options={typeAheadOptions} />
                    </div>;
                break;
            case 'type-ahead':
                filter =
                    <div className="text-filter type-ahead">
                        <div className="type-ahead-container">
                            <TypeAheadSearch options={typeAheadOptions} name={name} placeholder='Search domain name' handleSubmit={submitFilter} />
                        </div>
                    </div>;
                break;
            case 'checkbox':
                filter = <CheckboxFilter checkboxOptions={checkboxOptions} defaultCheckedOptions={defaultCheckedOptions} filterName={name} submitFilter={submitFilter} />
                break;
        }
        return filter;
    }

    const filter = chooseFilter();

    return (
        <div className="filter-wrapper" data-testid="filter">
            <div className="header">
                <button onClick={() => closeFilter()} >
                    <LeftArrow />
                </button>
                <span className="title">{title}</span>
            </div>
            {filter}
        </div>
    );
};

export default Filter;